import type { ReactNode, Ref } from 'react';
import s from './PageHeader.module.css';

/**
 * The title block every screen opens with: the seal masthead rule, an optional eyebrow
 * line, an h1 with an optional action cluster on the right, and an optional description
 * under them both.
 *
 * `mastheadRule` is a global class from global.css and one of the five allow-listed seal
 * uses. It is decorative: it is not a divider between sections, and it never marks state.
 *
 * The description sits under the title row rather than beside the actions, so on a phone
 * the h1 and its action share a line and a long description never pushes the action down.
 *
 * `eyebrow` is the line above the title: on a detail page, the link back to the list it
 * belongs to, which is also what says what kind of thing the h1 names. A link in it takes
 * the global link treatment, ink and underlined, with no arrow or chevron.
 *
 * `documentTitle` is the browser tab's title, rendered verbatim as a <title>, which React
 * hoists into <head>. Without it nothing is rendered and the tab keeps index.html's. A
 * route passes a static string, never its h1: a key or tenant name must not reach the tab
 * title, history or analytics.
 *
 * `headingRef` is for a page that moves focus to its title when a change takes away the
 * control that was used. Only then is the h1 given tabIndex -1: a heading is not a stop
 * in the tab order, and becomes focusable only so that script can put focus on it.
 */
export function PageHeader({
  title,
  description,
  actions,
  headingRef,
  eyebrow,
  documentTitle,
}: {
  title: string;
  description?: ReactNode;
  actions?: ReactNode;
  headingRef?: Ref<HTMLHeadingElement>;
  eyebrow?: ReactNode;
  documentTitle?: string;
}) {
  return (
    <header className={s.header}>
      {documentTitle ? <title>{documentTitle}</title> : null}
      <hr className="mastheadRule" />
      {eyebrow ? <p className={s.eyebrow}>{eyebrow}</p> : null}
      <div className={s.row}>
        <div className={s.titleBlock}>
          <h1 className={s.title} {...(headingRef ? { ref: headingRef, tabIndex: -1 } : {})}>
            {title}
          </h1>
        </div>
        {actions ? <div className={s.actions}>{actions}</div> : null}
      </div>
      {description ? <p className={s.description}>{description}</p> : null}
    </header>
  );
}

export default PageHeader;
